import { getIntrinsicElementProps, slot, useFocusableGroup, useId } from '@fluentui/react-components';
import { usePromptStarterListContext } from '../../../contexts/PromptStarterListContext';
import type { PromptStarterProps, PromptStarterState } from './PromptStarter.types';

/**
 * Create the state required to render PromptStarter.
 *
 * The returned state can be modified with hooks such as usePromptStarterStyles_unstable,
 * before being passed to renderPromptStarter_unstable.
 *
 * @param props - props from this instance of PromptStarter
 * @param ref - reference to root HTMLElement of PromptStarter
 */
export const usePromptStarter_unstable = (
  props: PromptStarterProps,
  ref: React.Ref<HTMLDivElement>,
): PromptStarterState => {
  const id = useId('prompt-starter');
  const focusAttrs = useFocusableGroup();

  const numberOfColumns = usePromptStarterListContext(ctx => ctx.numberOfColumns);
  // if this is defined, the context exists and the Prompt is within PromptStarterList
  const isWithinList = typeof numberOfColumns === 'number';

  const isSingleColumn = numberOfColumns === 1;

  const state: PromptStarterState = {
    isSingleColumn,
    components: {
      root: 'div',
      primaryAction: 'button',
      icon: 'span',
      category: 'span',
      prompt: 'span',
      reasonMarker: 'span',
      actions: 'span',
    },
    root: slot.always(
      getIntrinsicElementProps('div', {
        ref,
        role: isWithinList ? 'listitem' : undefined,
        ...focusAttrs,
        ...props,
      }),
      { elementType: 'div' },
    ),
    primaryAction: slot.always(props.primaryAction, {
      defaultProps: {
        'aria-labelledby': `${id}-category ${id}-prompt`,
        'aria-describedby': `${id}-icon ${id}-reasonMarker`,
      },
      elementType: 'button',
    }),
    icon: !isSingleColumn
      ? slot.optional(props.icon, {
          defaultProps: { id: `${id}-icon` },
          elementType: 'span',
        })
      : undefined,
    category: slot.optional(props.category, {
      defaultProps: { id: `${id}-category` },
      elementType: 'span',
    }),
    prompt: slot.always(props.prompt, {
      defaultProps: { id: `${id}-prompt` },
      elementType: 'span',
    }),
    reasonMarker: !isSingleColumn
      ? slot.optional(props.reasonMarker, {
          defaultProps: { id: `${id}-reasonMarker` },
          elementType: 'span',
        })
      : undefined,
    actions: !isSingleColumn ? slot.optional(props.actions, { elementType: 'span' }) : undefined,
  };

  return state;
};
